import React, { useCallback, useEffect, useState } from "react";
import SettingsIcon from "../../../../Assets/SettingsPages/SettingsIcon.svg";
import EmailIcon from "../../../../Assets/SettingsPages/EmailIcon.svg";
import DollarIcon from "../../../../Assets/SettingsPages/DollarIcon.svg";
import AutomatedClientCreationIcon from "../../../../Assets/SettingsPages/AutomatedClientCreationIcon.svg";
import { useSettingsStore } from "../../../../Store/Settings";
import { useTeamStore } from "../../../../Store/Team";
import { useAuth0 } from "@auth0/auth0-react";
import { SettingsPatch } from "../../../../Contracts/Settings";
import ConfigureEmailPreviewSection from "./ConfigureEmailPreviewSection";
import PostMarkSignatureManagement from "./PostMarkSignatureManagement";
import Toggle from "../../../../Components/Atoms/Toggle";
import Uppy, { Meta } from "@uppy/core";
import "@uppy/core/dist/style.css";
import "@uppy/dashboard/dist/style.css";
import { DashboardModal } from "@uppy/react";
import AwsS3Multipart, { AwsBody } from "@uppy/aws-s3";
import { SettingsPagesChildTabProps } from "../index";

const ConfigureBrandingTab: React.FC<SettingsPagesChildTabProps> = ({ currentTabWorkDone }) => {
  const { isLoading, settings, getEmailsSettings, getSettings, updateSettings } =
    useSettingsStore();
  const { currentTeam, getCurrentTeam } = useTeamStore();
  const { getAccessTokenSilently } = useAuth0();
  const [dirtySettings, setDirtySettings] = useState<SettingsPatch>({});
  const [logoUploadModalOpen, setLogoUploadModalOpen] = useState(false);
  const sanitizeFilename = (name?: string) => name?.replace(/[^0-9a-z.\u0020]/gi, "");

  const onBeforeFileAddedFunction: any = (currentFile: any, _files: any) => {
    return {
      ...currentFile,
      name: sanitizeFilename(currentFile?.name) ?? "",
      meta: {
        ...currentFile.meta,
        name: sanitizeFilename(currentFile?.meta?.name) ?? ""
      }
    };
  };

  const uppy = new Uppy<Meta, AwsBody>({
    autoProceed: true,
    id: "MyUppy",
    restrictions: {
      maxFileSize: 1000000, // 1MB
      maxNumberOfFiles: 1,
      allowedFileTypes: ["image/*"]
    },
    onBeforeFileAdded: onBeforeFileAddedFunction
  });

  uppy.use(AwsS3Multipart, {
    endpoint: "https://files.zomentum.com/",
    id: "AwsS3Multipart",
    shouldUseMultipart: true
  });

  uppy.on("upload-success", (file: any, response: any) => {
    setDirtySettings({
      ...dirtySettings,
      branding_company_logo_s3_url: response.uploadURL
    });
    setLogoUploadModalOpen(false);
  });

  const refreshThings = useCallback(async () => {
    await Promise.all([
      getSettings(getAccessTokenSilently),
      getCurrentTeam(getAccessTokenSilently),
      getEmailsSettings(getAccessTokenSilently)
    ]);
  }, [getAccessTokenSilently, getSettings, getEmailsSettings, getCurrentTeam]);

  useEffect(() => {
    refreshThings();
  }, [refreshThings]);

  const saveAndContinue = async () => {
    if (Object.keys(dirtySettings).length > 0) {
      const success = await updateSettings(getAccessTokenSilently, dirtySettings);
      if (!success) {
        setDirtySettings({});
        return;
      }
    }
    setDirtySettings({});
    currentTabWorkDone();
  };

  const updatedSettings = { ...settings, ...dirtySettings };
  const isDirty = dirtySettings ? Object.keys(dirtySettings).length > 0 : false;

  return (
    <div className={`flex flex-col ${isLoading ? "cursor-progress" : ""}`}>
      <div className="grid grid-cols-5 h-[calc(100vh-16rem)]">
        <div className="col-span-3 overflow-y-auto">
          <div className="mt-8">
            <div className="flex">
              <img src={SettingsIcon} alt="Brand Configuration" />
              <div className="font-semibold text-gray-900 ml-2">Brand Configuration</div>
            </div>
            <div className="ml-8">
              <div className="text-gray-700 mt-2">
                All the new emails and payment portal will have below name and image
              </div>
              <div className="grid md:grid-cols-5 grid-cols-1 mb-6 mt-8 items-center">
                <label className="col-span-1">Company Name</label>
                <input
                  type="text"
                  name="branding_company_name"
                  className={`col-span-3 w-full border rounded px-3 py-2 border-gray-300 ${
                    isLoading ? "bg-gray-100 animate-pulse" : ""
                  }`}
                  value={updatedSettings?.branding_company_name}
                  onChange={e =>
                    setDirtySettings({
                      ...dirtySettings,
                      branding_company_name: e.target.value
                    })
                  }
                />
              </div>
              <div className="grid md:grid-cols-5 grid-cols-1 mb-6 items-center">
                <label className="col-span-1">Company Logo</label>

                <div className="col-span-3 w-full">
                  <div className="flex justify-between items-center">
                    <div>
                      <button
                        className="rounded border px-2 py-1 hover:bg-white-smoke hover:scale-105"
                        onClick={() => setLogoUploadModalOpen(true)}
                      >
                        Add File
                      </button>
                      <DashboardModal
                        uppy={uppy}
                        open={logoUploadModalOpen}
                        onRequestClose={() => setLogoUploadModalOpen(false)}
                        proudlyDisplayPoweredByUppy={false}
                      />
                    </div>
                    {updatedSettings.branding_company_logo_s3_url &&
                      updatedSettings.branding_company_logo_s3_url.length > 0 && (
                        <img
                          src={updatedSettings.branding_company_logo_s3_url}
                          alt="Company Logo"
                          className="w-8 h-8"
                        />
                      )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-8">
            <div className="flex">
              <img src={EmailIcon} alt="Email Default Style" />
              <div className="font-semibold text-gray-900 ml-2">Email Default Style</div>
            </div>
            <div className="ml-8">
              <div className="text-gray-600 mt-2">
                All emails sent by Zomentum Payments to your clients will have the below style.
              </div>
              <div className="grid md:grid-cols-5 grid-cols-1 mb-6 mt-8 items-center">
                <div className="col-span-1 text-gray-950">Button color</div>

                <input
                  type="color"
                  name="email_button_color"
                  id="email_button_color"
                  className={`${isLoading ? " animate-pulse" : ""}`}
                  value={updatedSettings?.email_button_color || "black"}
                  onChange={e =>
                    setDirtySettings({
                      ...dirtySettings,
                      email_button_color: e.target.value
                    })
                  }
                />
              </div>
              <div className="grid md:grid-cols-5 grid-cols-1 mt-8">
                <div className="col-span-1 text-gray-950">Link color</div>

                <input
                  type="color"
                  name="email_link_color"
                  id="link_color"
                  className={`${isLoading ? " animate-pulse" : ""}`}
                  value={updatedSettings?.email_link_color || "black"}
                  onChange={e => {
                    setDirtySettings({
                      ...dirtySettings,
                      email_link_color: e.target.value
                    });
                  }}
                />
              </div>

              <div className="grid md:grid-cols-5 grid-cols-1 mt-8">
                <label className="col-span-1 text-gray-950 mr-4">
                  Include Invoice PDFs that are generated by your accounting tool
                </label>
                <select
                  name="include_invoice_pdf_in_emails"
                  className="px-3 mr-2 py-2 my-auto border border-gray-300 rounded h-fit"
                  value={updatedSettings?.include_invoice_pdf_in_emails ? "1" : "0"}
                  onChange={e =>
                    setDirtySettings({
                      ...dirtySettings,
                      include_invoice_pdf_in_emails: e.target.value === "1"
                    })
                  }
                >
                  <option value="1">Yes</option>
                  <option value="0">No</option>
                </select>
              </div>

              <div className="hidden animate-fade-in-down fixed bg-gray-800 bg-opacity-75 z-50 justify-center items-center inset-0">
                <div className="relative p-6 w-fit h-fit mx-auto mt-22 max-w-[24rem] bg-gray-100 rounded overflow-hidden">
                  <h2 className="text-l text-gray-950 mb-2">Exclude invoice PDFs from emails</h2>
                  <p className="text-gray-900"></p>
                  <p>
                    Select 'no' only if you are sending invoices directly from another platform,
                    e.g. your PSA or a 3rd party invoicing system, and therefore don't want the
                    client to see the PDF invoice generated by your accounting tool.
                  </p>
                  <p>
                    NOTE: It's critical that the client receives an invoice and you may need to
                    modify the wording on your email notification templates accordingly.
                    <br />
                    By selecting 'no' invoice PDFs will not be rendered on the checkout page and
                    will also be hidden from the client portal.
                  </p>
                  <p></p>

                  <button type="button" className="btn-primary px-5 float-right mt-3">
                    Got It
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-8">
            <div className="flex">
              <img src={DollarIcon} alt="Payment Page Default Style" />
              <div className="font-semibold text-gray-900 text-m ml-2">
                Payment Page Default Style
              </div>
            </div>
            <div className="ml-8">
              <div className="text-gray-700 mt-2">
                The payment pages will have the below style by default
              </div>
              <div className="grid md:grid-cols-5 grid-cols-1 mt-8 mb-8">
                <div className="col-span-1 text-gray-950">Button color</div>

                <input
                  type="color"
                  name="payment_button_color"
                  id="payment_button_color"
                  className={`${isLoading ? " animate-pulse" : ""}`}
                  value={updatedSettings?.payment_button_color || "black"}
                  onChange={e =>
                    setDirtySettings({
                      ...dirtySettings,
                      payment_button_color: e.target.value
                    })
                  }
                />
              </div>
            </div>
          </div>
          {currentTeam?.early_access_features?.["customer-auto-mapping"] && (
            <div className="my-8">
              <div className="flex">
                <img src={AutomatedClientCreationIcon} alt="Automated Client Creation" />
                <div className="font-semibold text-gray-900 ml-2">Automated Client Creation</div>
                <Toggle
                  checked={updatedSettings.client_creation_automated || false}
                  isLoading={isLoading}
                  onChange={enabled =>
                    setDirtySettings({
                      ...dirtySettings,
                      client_creation_automated: enabled
                    })
                  }
                  className="ml-2"
                />
              </div>
              <div className="text-gray-700 mt-2 ml-8 w-1/2">
                Zomentum Payments will automatically create a client for incoming clients from Grow
                if no matching client name is found or if it hasn't been mapped to QuickBooks/Xero.
              </div>
            </div>
          )}
          <PostMarkSignatureManagement />
        </div>

        <ConfigureEmailPreviewSection
          brandingCompanyName={updatedSettings?.branding_company_name}
          logoUrl={updatedSettings?.branding_company_logo_s3_url}
          emailLinkColor={updatedSettings?.email_link_color}
          emailButtonColor={updatedSettings?.email_button_color}
          paymentButtonColor={updatedSettings?.payment_button_color}
        />
      </div>
      <footer className="items-end w-full border-gray-200 border-t pt-4 pl-2">
        <button
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-700"
          onClick={saveAndContinue}
        >
          {isDirty ? "Save and Continue" : "Next"}
        </button>
        {isDirty && (
          <button
            className="hover:text-red-600 text-gray-500 px-4 py-2 ml-2"
            onClick={() => setDirtySettings({})}
          >
            Discard
          </button>
        )}
      </footer>
    </div>
  );
};

export default ConfigureBrandingTab;
